import { useEffect, useRef, useState } from "react"
import { useTripQuery, useTripCountQuery, useDownloadableTripsQuery, useTripExportStatus } from "../contexts/TripContext"
import type { Trip, TripCount, TripExportResponse, TripQueryState } from "../types/trip"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "./ui/pagination"
import { usePersistedState } from "../hooks/usePersistedState"
import { useSubmittedTripQuery } from "../contexts/SubmittedTripQueryContext"
import { formatDate } from "../utils/formatDate"
import { useNavigate } from "react-router-dom"
import { useToast } from "../hooks/use-toast"
import { useTrackNavigation } from "../hooks/useTrackNavigation"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Spinner } from "./ui/spinner"
import { Download, RefreshCw } from "lucide-react"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { useCSVDownloadFromURL } from "../hooks/useCSVDownload"
import { AlertView } from "./common/Alert"
import { DataTableSkeleton } from "./TableSkeleton"
import { convertDateFormat } from "../utils/formatDate"
import { convertToYesNo, isTripCancelled } from "../utils/formatBoolean"
import { convertToDisplayLicensePlate } from "../utils/formatLicensePlate"
import { sanitize, sanitizeEmail, validateEmail, isValidUUID } from "../utils/sanitizeInput"
import { queryErrorAlertMessage } from "../utils/queryErrorMessage"
import { getDateRangeError, inclusiveDateRangeDays, isCalendarDate } from "../utils/dateRange"

const sameQueryState = (left: TripQueryState, right: TripQueryState): boolean =>
  JSON.stringify(left) === JSON.stringify(right)

const PAGING_KEYS: ReadonlyArray<keyof TripQueryState> = ["currentPage", "itemsPerPage"]

// Paging never changes which trips match, so only the filter fields decide
// whether the form has drifted from the submitted search.
const sameFilters = (left: TripQueryState, right: TripQueryState): boolean =>
  (Object.keys({ ...left, ...right }) as Array<keyof TripQueryState>)
    .filter((key) => !PAGING_KEYS.includes(key))
    .every((key) => left[key] === right[key])

const PAGE_SIZE_OPTIONS = [10, 20]
const FLAG_FILTER_VALUES = ["all", "1", "0"]
const TEXT_FILTER_KEYS = [
  "phoneNumber", "email", "licensePlate", "tableFilter", "tripIdFilter", "transponderId", "uuidFilter",
] as const

// A saved form that is not the shape this page writes (a null, an array, a
// partial or edited object) falls back to the defaults instead of crashing
// the page on the first field it reads.
const isStoredQueryState = (value: unknown): value is TripQueryState => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const state = value as TripQueryState
  return (state.startDate === "" || isCalendarDate(state.startDate)) &&
    (state.endDate === "" || isCalendarDate(state.endDate)) &&
    Number.isSafeInteger(state.currentPage) && state.currentPage > 0 &&
    PAGE_SIZE_OPTIONS.includes(state.itemsPerPage) &&
    (state.autoCloseFilter === null || state.autoCloseFilter === 0 || state.autoCloseFilter === 1) &&
    FLAG_FILTER_VALUES.includes(state.grantedFilter) && FLAG_FILTER_VALUES.includes(state.validFilter) &&
    TEXT_FILTER_KEYS.every((key) => typeof state[key] === "string")
}

export const MAX_TRIP_EXPORT_ROWS = 500_000
export const MAX_TRIP_EXPORT_DATE_RANGE_DAYS = 31
const EXPORT_STATUS_POLL_MS = 2_000
const EXPORT_STATUS_TIMEOUT_MS = 30 * 60 * 1_000

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds))

const TripQueryComponent = (): JSX.Element => {
  useTrackNavigation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { handleDownload: handleDownloadFromURL } = useCSVDownloadFromURL()
  const getTripExportStatus = useTripExportStatus()
  const isMounted = useRef(true)
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState<string | null>(null)
  const [showUnappliedFiltersHint, setShowUnappliedFiltersHint] = useState(false)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])
  const [emailFilterError, setEmailFilterError] = useState<string | null>(null)
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null)
  const [tripIdFilterError, setTripIdFilterError] = useState<string | null>(null)
  const [licensePlateError, setLicensePlateError] = useState<string | null>(null)
  const [uuidFilterError, setUuidFilterError] = useState<string | null>(null)
  const [dateRangeError, setDateRangeError] = useState<string | null>(null)

  const getDefaultState = (): TripQueryState => {
    const defaultEndDate = new Date()
    const defaultStartDate = new Date(defaultEndDate)
    defaultStartDate.setDate(defaultStartDate.getDate() - 2)
    return {
      currentPage: 1,
      itemsPerPage: 10,
      phoneNumber: "",
      email: "",
      autoCloseFilter: null,
      startDate: formatDate(defaultStartDate),
      endDate: formatDate(defaultEndDate),
      licensePlate: "",
      grantedFilter: "all",
      validFilter: "all",
      tableFilter: "",
      tripIdFilter: "",
      transponderId: "",
      uuidFilter: "",
    }
  }
  const [queryState, setQueryState] = usePersistedState<TripQueryState>({
    storageKey: "tripQueryState",
    defaultValue: getDefaultState(),
    validate: isStoredQueryState,
  })
  const [submittedQueryState, setSubmittedQueryState] = useSubmittedTripQuery()
  const activeQueryState = submittedQueryState ?? queryState
  const queryEnabled = submittedQueryState !== null
  const hasUnappliedFilters = submittedQueryState !== null && !sameFilters(queryState, submittedQueryState)

  useEffect(() => {
    setShowUnappliedFiltersHint(false)
  }, [queryState])

  useEffect(() => {
    setDateRangeError(null)
  }, [queryState.startDate, queryState.endDate])

  const {
    isError: tripQueryIsError, error: tripQueryError, data, isFetching: tripQueryIsFetching, refetch,
  } = useTripQuery(activeQueryState, queryEnabled)

  // Pagination does not affect COUNT(*). Keeping these values stable avoids a
  // second large-range count every time the user moves between result pages
  // or changes the page size.
  const countQueryState = {
    ...activeQueryState,
    currentPage: 1,
    itemsPerPage: 1,
  }
  const { data: tripCount, isError: tripCountIsError, error: tripCountError, refetch: tripCountRefetch } = useTripCountQuery(countQueryState, queryEnabled)

  const {
    isFetching: downloadableTripsIsFetching,
    refetch: downloadableTripsRefetch,
  } = useDownloadableTripsQuery(activeQueryState, false)
  const isError = queryEnabled && (tripQueryIsError || tripCountIsError)
  const isFetching = queryEnabled && tripQueryIsFetching
  const trips = queryEnabled ? data as Trip[] || [] : []
  const tripCountData = queryEnabled ? tripCount as TripCount[] : undefined
  const resultPageSize = submittedQueryState?.itemsPerPage ?? queryState.itemsPerPage
  const totalCount = tripCountData?.[0]?.totalCount
  const canGoToNextPage = totalCount === undefined || totalCount === null
    ? trips.length === resultPageSize
    : queryState.currentPage < Math.ceil(Number(totalCount) / resultPageSize)

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage === queryState.currentPage) return
    if (newPage > queryState.currentPage && !canGoToNextPage) return
    setQueryState((prev) => ({ ...prev, currentPage: newPage }))
    setSubmittedQueryState((prev) => prev ? { ...prev, currentPage: newPage } : prev)
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setQueryState((prev) => ({ ...prev, itemsPerPage: newItemsPerPage, currentPage: 1 }))
    setSubmittedQueryState((prev) => prev ? { ...prev, itemsPerPage: newItemsPerPage, currentPage: 1 } : prev)
  }

  const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // Only allow alphanumeric characters (letters and numbers)
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()

    if (value.length > 10) {
      setLicensePlateError("Invalid license plate")
    } else {
      setLicensePlateError(null)
    }

    setQueryState((prev) => ({
      ...prev,
      licensePlate: value.slice(0, 10),
    }))
  }

  const handleTripIdFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = sanitize(e.target.value)
    if (value.length === 0) {
      setTripIdFilterError(null)
      setQueryState((prev) => ({ ...prev, tripIdFilter: value }))
    } else if (value.length < 24) {
      setTripIdFilterError("Trip ID must be 24 characters long")
      setQueryState((prev) => ({ ...prev, tripIdFilter: value }))
    } else {
      setTripIdFilterError(null)
      setQueryState((prev) => ({ ...prev, tripIdFilter: value }))
    }
  }

  const resetToDefaults = () => {
    const defaults = getDefaultState()
    setQueryState(defaults)
    setSubmittedQueryState(null)
    setTripIdFilterError(null)
    setEmailFilterError(null)
    setPhoneNumberError(null)
    setLicensePlateError(null)
    setUuidFilterError(null)
    setDateRangeError(null)
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/\D/g, "")
    if (value.length === 0) {
      setPhoneNumberError(null)
      setQueryState((prev) => ({ ...prev, phoneNumber: value }))
    } else if (value.length < 10) {
      setPhoneNumberError("Phone number must be at least 10 digits long")
      setQueryState((prev) => ({ ...prev, phoneNumber: value }))
    } else if (value.length === 10) {
      setPhoneNumberError(null)
      setQueryState((prev) => ({ ...prev, phoneNumber: value }))
    } else {
      setPhoneNumberError("Phone number cannot exceed 10 digits")
      setQueryState((prev) => ({ ...prev, phoneNumber: value.slice(0, 10) }))
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = sanitizeEmail(e.target.value)
    if (value) {
      setEmailFilterError(null)
    }

    if (value && !validateEmail(value)) {
      setEmailFilterError("Please enter a valid email address")
    } else {
      setEmailFilterError(null)
    }

    setQueryState((prev) => ({ ...prev, email: value }))
  }

  const handleTransponderIdChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = sanitize(e.target.value)
    setQueryState((prev) => ({ ...prev, transponderId: value }))
  }

  const handleAutoCloseChange = (value: string): void => {
    setQueryState((prev) => ({ ...prev, autoCloseFilter: value === 'all' ? null : Number(value) }))
  }

  const handleInCorridorChange = (value: string): void => {
    setQueryState((prev) => ({ ...prev, grantedFilter: value }))
  }

  const handleValidChange = (value: string): void => {
    setQueryState((prev) => ({ ...prev, validFilter: value }))
  }

  const handleUUIDChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = sanitize(e.target.value)
    if (value.length === 0) {
      setUuidFilterError(null)
    } else if (value.length < 20) {
      setUuidFilterError("UUID must be 20 characters long")
    } else if (value.length > 20) {
      setUuidFilterError("UUID must be 20 characters long")
    } else if (!isValidUUID(value)) {
      setUuidFilterError("Invalid UUID")
    } else {
      setUuidFilterError(null)
    }
    setQueryState((prev) => ({ ...prev, uuidFilter: value }))
  }

  const handleFindClick = async (): Promise<void> => {
    if (emailFilterError || phoneNumberError || tripIdFilterError || licensePlateError || uuidFilterError) {
      return
    }

    // Required, real calendar dates in order and within a year: anything else
    // is answered here, not by the backend's generic 400 or an empty window.
    const dateError = getDateRangeError(queryState.startDate, queryState.endDate)
    if (dateError) {
      setDateRangeError(dateError)
      return
    }
    setDateRangeError(null)

    const nextQueryState = { ...queryState, currentPage: 1 }
    setQueryState(nextQueryState)

    // A changed snapshot is fetched automatically when its query keys become
    // enabled. An identical Find/Retry is an explicit refresh; run the page
    // and count requests concurrently and only once each.
    if (submittedQueryState && sameQueryState(submittedQueryState, nextQueryState)) {
      await Promise.all([refetch(), tripCountRefetch()])
      return
    }

    setSubmittedQueryState(nextQueryState)
  }

  const handleDownloadCSV = async () => {
    if (hasUnappliedFilters) {
      setShowUnappliedFiltersHint(true)
      return
    }

    const dateRangeDays = inclusiveDateRangeDays(activeQueryState.startDate, activeQueryState.endDate)
    if (dateRangeDays > MAX_TRIP_EXPORT_DATE_RANGE_DAYS) {
      toast({
        title: "Download unavailable",
        description: `Downloads are limited to ${MAX_TRIP_EXPORT_DATE_RANGE_DAYS} consecutive days.`,
        variant: "destructive",
      })
      return
    }

    const totalRows = Number(tripCountData?.[0]?.totalCount ?? 0)
    if (totalRows > MAX_TRIP_EXPORT_ROWS) {
      toast({
        title: "Download unavailable",
        description: `This export contains ${totalRows.toLocaleString()} trips. The limit is ${MAX_TRIP_EXPORT_ROWS.toLocaleString()}.`,
        variant: "destructive",
      })
      return
    }

    setIsExporting(true)
    setExportProgress("Queueing export...")
    try {
      const startResult = await downloadableTripsRefetch()
      if (startResult.error) throw startResult.error
      const started = startResult.data as TripExportResponse | undefined
      if (!started?.jobId) throw new Error("The export job could not be started.")

      const pollStartedAt = Date.now()
      while (isMounted.current && Date.now() - pollStartedAt < EXPORT_STATUS_TIMEOUT_MS) {
        const status = await getTripExportStatus(started.jobId)
        if (status.status === "complete") {
          if (!status.resultUrl) throw new Error("The completed export is no longer available.")
          await handleDownloadFromURL({
            getData: () => status.resultUrl || "",
            filename: status.filename || "trip_data.csv",
          })
          return
        }
        if (status.status === "empty") {
          toast({
            title: "No trips found",
            description: "No trips found for the selected filters",
            variant: "destructive",
          })
          return
        }
        if (status.status === "failed" || status.status === "rejected") {
          throw new Error(status.message || "The export could not be completed.")
        }

        if (isMounted.current) {
          const progress = status.totalRows && status.processedRows !== undefined
            ? `Preparing ${status.processedRows.toLocaleString()} of ${status.totalRows.toLocaleString()} trips...`
            : status.status === "counting"
              ? "Checking export size..."
              : "Waiting for export..."
          setExportProgress(progress)
        }
        await wait(EXPORT_STATUS_POLL_MS)
      }

      if (isMounted.current) {
        throw new Error("The export is taking too long. Please narrow the filters and try again.")
      }
    } catch (error) {
      if (isMounted.current) {
        toast({
          title: "Export failed",
          description: error instanceof Error ? error.message : "The export could not be completed.",
          variant: "destructive",
        })
      }
    } finally {
      if (isMounted.current) {
        setIsExporting(false)
        setExportProgress(null)
      }
    }
  }

  const handleRowClick = (trip: Trip): void => {
    navigate(`/trips/${trip.trip_id}`, {
      state: {
        tripData: trip,
      },
    })
  }

  return (
    <div className="top-margin p-4">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(auto,900px),auto] gap-5 mb-2">
        <Card className="bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="flex justify-between items-center">
              <span>Filters</span>
              <div className="text-right">
                <p className="text-sm font-medium text-muted-foreground">Trip Count</p>
                <div className="bg-primary/10 px-2 py-1 rounded-md">
                  <p
                    className="text-lg font-semibold text-primary"
                    data-testid="trip-count"
                    role="status"
                    aria-label="Trip count"
                  >
                    {tripCountData?.[0]?.totalCount ?? 0}
                  </p>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="grid grid-cols-[2fr,1fr] gap-14">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <label htmlFor="phoneNumber" className="text-sm font-medium w-1/3">
                    Phone Number
                  </label>
                  <div className="w-2/3">
                    <Input
                      id="phoneNumber"
                      className="w-full"
                      type="tel"
                      placeholder="Enter phone #"
                      value={queryState.phoneNumber}
                      onChange={handlePhoneNumberChange}
                    />
                    {phoneNumberError && <p className="text-red-500 text-sm">{phoneNumberError}</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="email" className="text-sm font-medium w-1/3">
                    Email
                  </label>
                  <div className="w-2/3">
                    <Input
                      id="email"
                      className="w-full"
                      type="email"
                      placeholder="Enter email"
                      value={queryState.email}
                      onChange={handleEmailChange}
                    />
                    {emailFilterError && <p className="text-red-500 text-sm">{emailFilterError}</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="licensePlate" className="text-sm font-medium w-1/3">
                    License Plate
                  </label>
                  <div className="w-2/3">
                    <Input
                      id="licensePlate"
                      className="w-full"
                      placeholder="Enter license plate"
                      value={queryState.licensePlate}
                      onChange={handleLicensePlateChange}
                      maxLength={10}
                    />
                    {licensePlateError && <p className="text-red-500 text-sm">{licensePlateError}</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="tripIdFilter" className="text-sm font-medium w-1/3">
                    Trip ID
                  </label>
                  <div className="w-2/3">
                    <Input
                      id="tripIdFilter"
                      className="w-full"
                      placeholder="Enter Trip ID"
                      value={queryState.tripIdFilter}
                      onChange={handleTripIdFilterChange}
                      maxLength={24}
                    />
                    {tripIdFilterError && <p className="text-red-500 text-sm">{tripIdFilterError}</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="transponderId" className="text-sm font-medium w-1/3">
                    Transponder ID
                  </label>
                  <Input
                    id="transponderId"
                    className="w-2/3"
                    type="number"
                    placeholder="Enter Transponder ID"
                    value={queryState.transponderId}
                    onChange={handleTransponderIdChange}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="UUID" className="text-sm font-medium w-1/3">
                    UUID
                  </label>
                  <div className="w-2/3">
                    <Input
                      id="UUID"
                      className="w-full"
                      type="text"
                      placeholder="Enter UUID"
                      value={queryState.uuidFilter}
                      onChange={handleUUIDChange}
                    />
                    {uuidFilterError && <p className="text-red-500 text-sm">{uuidFilterError}</p>}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <label htmlFor="autoClose" className="text-sm font-medium w-1/2">
                    Auto-Close
                  </label>
                  <Select
                    value={queryState.autoCloseFilter?.toString() ?? 'all'}
                    onValueChange={handleAutoCloseChange}
                  >
                    <SelectTrigger id="autoClose" className="w-1/2">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all" className="hover:bg-slate-100 cursor-pointer">
                        All
                      </SelectItem>
                      <SelectItem value="1" className="hover:bg-slate-100 cursor-pointer">
                        True
                      </SelectItem>
                      <SelectItem value="0" className="hover:bg-slate-100 cursor-pointer">
                        False
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="inCorridor" className="text-sm font-medium w-1/2">
                    In Corridor
                  </label>
                  <Select
                    value={queryState.grantedFilter}
                    onValueChange={handleInCorridorChange}
                  >
                    <SelectTrigger id="inCorridor" className="w-1/2">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all" className="hover:bg-slate-100 cursor-pointer">
                        All
                      </SelectItem>
                      <SelectItem value="1" className="hover:bg-slate-100 cursor-pointer">
                        Yes
                      </SelectItem>
                      <SelectItem value="0" className="hover:bg-slate-100 cursor-pointer">
                        No
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="valid" className="text-sm font-medium w-1/2">
                    Valid
                  </label>
                  <Select
                    value={queryState.validFilter}
                    onValueChange={handleValidChange}
                  >
                    <SelectTrigger id="valid" className="w-1/2">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all" className="hover:bg-slate-100 cursor-pointer">
                        All
                      </SelectItem>
                      <SelectItem value="1" className="hover:bg-slate-100 cursor-pointer">
                        Yes
                      </SelectItem>
                      <SelectItem value="0" className="hover:bg-slate-100 cursor-pointer">
                        No
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="flex flex-col justify-end gap-4">
          <Card className="bg-white w-full md:w-[350px]">
            <CardHeader className="pb-2">
              <CardTitle>Date Range</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label htmlFor="startDate" className="text-sm font-medium">
                      Start Date
                    </label>
                    <Input
                      value={queryState.startDate}
                      id="startDate"
                      type="date"
                      className="w-full"
                      onChange={(e) => setQueryState({ ...queryState, startDate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="endDate" className="text-sm font-medium">
                      End Date
                    </label>
                    <Input
                      value={queryState.endDate}
                      id="endDate"
                      type="date"
                      className="w-full"
                      onChange={(e) => setQueryState({ ...queryState, endDate: e.target.value })}
                    />
                  </div>
                </div>
                {dateRangeError && (
                  <p className="text-red-500 text-sm" role="alert" data-testid="date-range-error">
                    {dateRangeError}
                  </p>
                )}
                <Button onClick={handleFindClick} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  Find
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-2 md:w-[350px]">
            <Button
              onClick={resetToDefaults}
              variant="outline"
              className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground bg-green-600 text-white hover:bg-green-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleDownloadCSV}
              variant="outline"
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground bg-green-600 text-white hover:bg-green-700"
              disabled={isExporting || downloadableTripsIsFetching || trips.length === 0}
              title={hasUnappliedFilters ? "Press Find to apply the new filters" : undefined}
              data-testid="download-csv-button"
            >
              {isExporting || downloadableTripsIsFetching ? (
                <>
                  <Spinner size="small" className="mr-2" />
                  {exportProgress || "Preparing export..."}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </>
              )}
            </Button>
            {hasUnappliedFilters && showUnappliedFiltersHint && (
              <p className="text-sm text-amber-700" role="status" data-testid="unapplied-filters-hint">
                Filters changed — press Find to refresh the results before downloading.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 p-0">
        {isError ? (
          <AlertView
            message={queryErrorAlertMessage(tripQueryIsError ? tripQueryError : tripCountError)}
            onAction={handleFindClick}
            buttonTitle="Retry"
          />
        ) : isFetching ? (
          <DataTableSkeleton rows={queryState.itemsPerPage} columns={10} />
        ) : (
          <Card className="flex-1 bg-white pt-6">
            <CardContent className="pt-0 pb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trip ID</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead> End Date </TableHead>
                    <TableHead>Occupancy</TableHead>
                    <TableHead>In Corridor</TableHead>
                    <TableHead>Valid</TableHead>
                    <TableHead>Cancelled</TableHead>
                    <TableHead>Transponder ID</TableHead>
                    <TableHead>No GPS</TableHead>
                    <TableHead>License Plate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trips.map((trip: Trip) => (
                    <TableRow
                      key={trip.trip_id}
                      onClick={() => handleRowClick(trip)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          handleRowClick(trip)
                        }
                      }}
                      tabIndex={0}
                      aria-label={`View details for trip ${trip.trip_id}`}
                      className="cursor-pointer hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="trip-row"
                    >
                      <TableCell data-testid="trip_id">{trip.trip_id}</TableCell>
                      <TableCell data-testid="trip_start_time">{convertDateFormat(trip.trip_start_time)}</TableCell>
                      <TableCell data-testid="end_time">{convertDateFormat(trip.end_time)}</TableCell>
                      <TableCell data-testid="occupancy">{trip.occupancy}</TableCell>
                      <TableCell data-testid="in_corridor">{convertToYesNo(trip.in_corridor)}</TableCell>
                      <TableCell data-testid="valid">{convertToYesNo(trip.valid)}</TableCell>
                      <TableCell data-testid="cancelled">{isTripCancelled(trip)}</TableCell>
                      <TableCell data-testid="transponder_id">{trip.transponder_id}</TableCell>
                      <TableCell data-testid="no_gps">{convertToYesNo(trip.no_gps)}</TableCell>
                      <TableCell data-testid="license_plate">
                        {convertToDisplayLicensePlate(trip.license_plate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between w-full mt-4">
                <div>
                  <Select
                    value={`${queryState.itemsPerPage}`}
                    onValueChange={(value) => handleItemsPerPageChange(Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {PAGE_SIZE_OPTIONS.map((pageSize) => (
                        <SelectItem key={pageSize} value={`${pageSize}`} className="hover:bg-slate-100 cursor-pointer">
                          {pageSize} rows
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Pagination className="w-full justify-end">
                  <PaginationContent className="justify-end">
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={() => handlePageChange(Math.max(1, queryState.currentPage - 1))}
                        className={queryState.currentPage <= 1 ? "cursor-not-allowed opacity-50" : ""}
                      />
                    </PaginationItem>
                    <PaginationEllipsis />
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={() => handlePageChange(queryState.currentPage + 1)}
                        className={!canGoToNextPage ? "cursor-not-allowed opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default TripQueryComponent
